import { List } from "immutable";
// import syncUpdateActions from "../../constants/syncUpdateActions";
import { clone } from "lodash";
import { createSelector } from "reselect";

import { getDefaultIconForSyncType } from "../../constants/syncDocumentTypes";
import { SYNC_NARRATIVE_FOLDER_IDENTIFIER } from "../../constants/syncNarrativeFolderIdentifier";
import {
  syncStatusLowestSeverity,
  syncStatusOptions,
  syncStatusProperties,
  syncStatusSeverity
} from "../../constants/syncStatus";
import syncUpdateActions from "../../constants/syncUpdateActions";
import { definitionSelectors } from "../../modules/definition";
import { syncSelectors } from "../../modules/sync";
import { syncSettingsSelectors } from "../../modules/sync/settings";
import syncRepresentationOfTimelineFromStatusState from "../../sync/selectors/syncRepresentationOfTimelineFromStatusState";
import { immutableRecords } from "../../types";
// import syncUpdateActions from "../../constants/syncUpdateActions";

const MANUSCRIPT_ID = "MANUSCRIPT_ID";

const statusOfItem = (docUpdates, children, syncApplicationName) => {
  let status = docUpdates && docUpdates.status ? docUpdates.status : syncStatusOptions.DETECTED_NO_CHANGE;
  let statusProps = clone(syncStatusProperties[status]);

  if (children.size) {
    // Find the child with the highest ranked status
    const maxStatusChild = children.maxBy(c =>
      Math.max(syncStatusSeverity[c.childStatus], syncStatusSeverity[c.status])
    );
    // Update our child status
    statusProps.childStatus =
      syncStatusSeverity[maxStatusChild.childStatus] > syncStatusSeverity[maxStatusChild.status]
        ? maxStatusChild.childStatus
        : maxStatusChild.status;
  } else {
    statusProps.childStatus = syncStatusLowestSeverity;
  }

  // If child status has greater severity than our own status, change the iconColor and labelColor
  // but not the statusColor
  if (syncStatusSeverity[statusProps.childStatus] > syncStatusSeverity[statusProps.status]) {
    statusProps.labelColor = syncStatusProperties[statusProps.childStatus].labelColor;
  }

  // Add the application name to the label
  statusProps.statusLabel = syncSelectors.getSyncStatusLabelForStatus(statusProps.status, syncApplicationName);

  return statusProps;
};

const getLabelForTreeItem = (id, docUpdates, externalItems, localItems, defaultLabel) => {
  if (id === SYNC_NARRATIVE_FOLDER_IDENTIFIER) {
    return "Narrative";
  }

  let label = defaultLabel;
  if (!docUpdates) {
    label = localItems.has(id)
      ? localItems.get(id).label
      : externalItems.has(id)
        ? externalItems.get(id).label
        : defaultLabel;
  } else {
    // Do we want local or external?
    const titleUpdate = docUpdates.propertyUpdates.get("title");
    if (
      docUpdates.action === syncUpdateActions.APPLY_CREATE_TO_EXTERNAL ||
      docUpdates.action === syncUpdateActions.APPLY_DELETE_TO_LOCAL ||
      (titleUpdate && titleUpdate.action === syncUpdateActions.APPLY_LOCAL_TO_EXTERNAL)
    ) {
      label = localItems.has(id) ? localItems.get(id).label : defaultLabel;
    } else {
      label = externalItems.has(id) ? externalItems.get(id).label : defaultLabel;
    }
  }

  if (!label) {
    label = defaultLabel;
  }
  return label;
};

const getSyncTypeForTreeItem = (externalItems, localItems, id) => {
  if (externalItems.has(id)) {
    return externalItems.get(id).type;
  }
  if (localItems.has(id)) {
    return localItems.get(id).type;
  }
};

const getIconForTreeItem = (treeId, children, syncType, syncSettings, typesById) => {
  let icon = getDefaultIconForSyncType(syncType, children.size > 0);
  if (treeId) {
    if (treeId === SYNC_NARRATIVE_FOLDER_IDENTIFIER) {
      return "sync-book";
    }
    const folder = syncSettings.entityFolders.get(treeId);
    if (folder && typesById.get(folder.mainType)) {
      icon = typesById.get(folder.mainType).icon;
    }
  }
  return icon;
};

const displayTreeItemForItem = (
  treeItem,
  index,
  externalItems,
  localItems,
  lastSyncItems,
  statusUpdates,
  defaultLabel,
  syncSettings,
  typesById,
  syncApplicationName
) => {
  const id = treeItem.id;
  // First, convert the children so we know what statuses are bubbling up from underneath
  const children = treeItem.children.map((c, i) =>
    displayTreeItemForItem(
      c,
      i,
      externalItems,
      localItems,
      lastSyncItems,
      statusUpdates,
      defaultLabel,
      syncSettings,
      typesById,
      syncApplicationName
    )
  );
  const docUpdates = id ? statusUpdates.updatesById.get(id) : null;
  const statusProps = statusOfItem(docUpdates, children, syncApplicationName);

  const label = getLabelForTreeItem(id, docUpdates, externalItems, localItems, defaultLabel);
  const type = getSyncTypeForTreeItem(externalItems, localItems, id);
  const icon = getIconForTreeItem(id, children, type, syncSettings, typesById);

  //const icon = id ? (children.size ? "sync-folder" : "sync-document") : "sync-book";

  return immutableRecords
    .TreeViewItemRecord({
      id: id ? id : MANUSCRIPT_ID,
      label,
      children,
      icon,
      index
    })
    .merge(statusProps);
};

const getBinderTreeItems = createSelector(
  // Inputs
  syncSelectors.getExternalSyncRep,
  syncRepresentationOfTimelineFromStatusState,
  syncSelectors.getLastSyncRep,
  syncSelectors.getSyncStatus,
  syncSelectors.getSyncSettings,
  definitionSelectors.getTypesById,
  syncSettingsSelectors.getSyncApplicationName,
  // Output
  (origExternalRep, localRep, lastSyncRep, status, origSettings, typesById, syncApplicationName) => {
    const syncSettings = status.updatedSettings ? status.updatedSettings : origSettings;
    const externalRep = status.updatedExternalRep ? status.updatedExternalRep : origExternalRep;
    return displayTreeItemForItem(
      status.documentUpdates.visualMergeTree,
      0,
      externalRep.documents,
      localRep.documents,
      lastSyncRep.documents,
      status.documentUpdates,
      "Untitled",
      syncSettings,
      typesById,
      syncApplicationName
    ).children;
  }
);

const getBinderChangeCount = state => {
  if (!syncSettingsSelectors.getSyncEnabled(state)) {
    return 0;
  }
  const status = syncSelectors.getSyncStatus(state);
  return status.documentUpdates.updatesById.filter(
    update =>
      update.status !== syncStatusOptions.DETECTED_NO_CHANGE && update.status !== syncStatusOptions.DETECTED_IGNORE
  ).size;
};

const getBinderConflictCount = state => {
  if (!syncSettingsSelectors.getSyncEnabled(state)) {
    return false;
  }
  const status = syncSelectors.getSyncStatus(state);
  return status.documentUpdates.updatesById.filter(u => u.status === syncStatusOptions.DETECTED_CONFLICT).size;
};

const getBinderBadgeColor = state => {
  if (!syncSettingsSelectors.getSyncEnabled(state)) {
    return null;
  }
  return getBinderConflictCount(state) > 0 ? "statusError" : "statusWarn";
};

const getWarningsCount = state => {
  if (!syncSettingsSelectors.getSyncEnabled(state)) {
    return 0;
  }
  const status = syncSelectors.getSyncStatus(state);
  return status.disabledFields.size + status.disabledFolders.size + (status.updatedExternalRep ? 1 : 0);
};

const getWarningsBadgeColor = () => {
  return "statusWarn";
};

const fieldLabelForId = (fieldId, typesById, propertiesById, referencesById) => {
  switch (fieldId) {
    case "title":
      return "Title";
    case "synopsis":
      return "Synopsis";
    default: {
      if (typesById.has(fieldId)) {
        return typesById.get(fieldId).label;
      }
      if (propertiesById.has(fieldId)) {
        return propertiesById.get(fieldId).label;
      }
      if (referencesById.has(fieldId)) {
        return referencesById.get(fieldId).label;
      }
      return fieldId;
    }
  }
};

const getWarnings = state => {
  if (!syncSettingsSelectors.getSyncEnabled(state)) {
    return List();
  }
  const status = syncSelectors.getSyncStatus(state);
  const typesById = definitionSelectors.getTypesById(state);
  const propertiesById = definitionSelectors.getPropertiesById(state);
  const referencesById = definitionSelectors.getReferencesById(state);

  let result = List();

  if (status.disabledFields.size) {
    let labels = [];
    status.disabledFields.map((_reason, fieldId) => {
      const label = fieldLabelForId(fieldId, typesById, propertiesById, referencesById);
      labels.push(label);
    });
    const labelsString =
      labels.length <= 1 ? labels.join(", ") : labels.slice(0, -1).join(", ") + " and " + labels.slice(-1);
    result = result.push({
      label: `Syncing ${labelsString} ${labels.length > 1 ? "have" : "has"} been disabled`,
      subLabel: `Sync now to ensure your items are up to date.`,
      leftIcon: "statusWarning",
      leftIconColor: "statusWarn",
      value: "fieldDisabled"
    });
  }

  if (status.disabledFolders.size) {
    let labels = [];
    status.disabledFolders.map((_reason, typeId) => {
      const label = typesById.has(typeId) ? typesById.get(typeId).label : "Unknown folder";
      labels.push(label);
    });
    const labelsString =
      labels.length <= 1 ? labels.join(", ") : labels.slice(0, -1).join(", ") + " and " + labels.slice(-1);
    result = result.push({
      label: `Syncing ${labels.length > 1 ? "folders" : "folder"} ${labelsString} ${
        labels.length > 1 ? "have" : "has"
      } been disabled`,
      subLabel: `Ensure folder exists in legal position`,
      leftIcon: "statusWarning",
      leftIconColor: "statusWarn",
      value: "fieldDisabled"
    });
  }

  if (status.updatedExternalRep) {
    result = result.push({
      label: `Settings changed`,
      subLabel: `Sync now to create additional entity folders or metadata fields`,
      leftIcon: "statusWarning",
      leftIconColor: "statusWarn",
      value: "newSettings"
    });
  }

  if (status.illegalDocumentOrder) {
    result = result.push({
      label: `Invalid Document Order`,
      subLabel: `Ulysses does not support documents placed after folders within the same parent. Narrative will be sorted accordingly when syncing.`,
      leftIcon: "statusWarning",
      leftIconColor: "statusWarn",
      value: "invalidOrder"
    });
  }

  return result;
};

const getTrashedItemCount = state => {
  if (!syncSettingsSelectors.getSyncEnabled(state)) {
    return 0;
  }
  const status = syncSelectors.getSyncStatus(state);
  return status.documentUpdates.updatesById.filter(u => u.status === syncStatusOptions.DETECTED_DELETE_IN_LOCAL).size;
};

export default {
  getBinderTreeItems,
  getBinderChangeCount,
  getBinderBadgeColor,
  getBinderConflictCount,
  getWarningsCount,
  getWarningsBadgeColor,
  getWarnings,
  getTrashedItemCount,
  getSyncTypeForTreeItem,
  getIconForTreeItem
};
